
import { Component, Vue } from 'vue-property-decorator'
import Pagination from '@/components/Pagination/index.vue'
import { getAimUsers, getAimUserDaily, getAimStats, getAimUserRecords } from '@/api/aim'

@Component({
  name: 'AimData',
  components: {
    Pagination
  }
})
export default class extends Vue {
  private list: any[] = []
  private listLoading = true
  private total = 0
  private listQuery = {
    page: 1,
    limit: 20,
    userId: ''
  }
  private dateRange: string[] = []

  private stats: any = {}

  // Daily detail dialog
  private dialogVisible = false
  private detailLoading = false
  private selectedUserId = ''
  private selectedUserLabel = ''
  private dailyData: any[] = []
  private detailDateRange: string[] = []

  // Records detail dialog
  private recordsDialogVisible = false
  private recordsLoading = false
  private recordsDate = ''
  private recordsList: any[] = []
  private recordsTotal = 0
  private recordsQuery = {
    page: 1,
    limit: 50
  }

  // Sensor data dialog
  private sensorDialogVisible = false
  private sensorDataJson = ''

  created() {
    this.getList()
    this.fetchStats()
  }

  private async fetchStats() {
    try {
      const { data } = await getAimStats()
      if (data.status.code === 'success') {
        this.stats = data.data || {}
      }
    } catch (error) {
      console.error('Failed to load AIM stats:', error)
    }
  }

  private async getList() {
    this.listLoading = true
    try {
      const params: any = {
        page: this.listQuery.page,
        limit: this.listQuery.limit
      }
      if (this.listQuery.userId) {
        params.userId = this.listQuery.userId
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }

      const { data } = await getAimUsers(params)
      if (data.status.code === 'success') {
        this.list = data.data.users || []
        this.total = data.data.total || 0
      }
    } catch (error) {
      this.$message.error('Failed to load AIM users')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.getList()
  }

  private async handleViewUser(row: any) {
    this.selectedUserId = row.userId
    const name = [row.firstName, row.lastName].filter(Boolean).join(' ') || row.userId
    this.selectedUserLabel = `AIM Data - ${name}`
    this.detailDateRange = this.dateRange.length === 2 ? [...this.dateRange] : []
    this.dialogVisible = true
    await this.fetchUserDaily()
  }

  private async fetchUserDaily() {
    this.detailLoading = true
    try {
      const params: any = {}
      if (this.detailDateRange && this.detailDateRange.length === 2) {
        params.startDate = this.detailDateRange[0]
        params.endDate = this.detailDateRange[1]
      }

      const { data } = await getAimUserDaily(this.selectedUserId, params)
      if (data.status.code === 'success') {
        this.dailyData = data.data.days || []
      }
    } catch (error) {
      this.$message.error('Failed to load user daily data')
      console.error(error)
    } finally {
      this.detailLoading = false
    }
  }

  private async handleViewRecords(date: string) {
    this.recordsDate = date
    this.recordsQuery.page = 1
    this.recordsDialogVisible = true
    await this.fetchRecords()
  }

  private async fetchRecords() {
    this.recordsLoading = true
    try {
      const { data } = await getAimUserRecords(this.selectedUserId, {
        date: this.recordsDate,
        page: this.recordsQuery.page,
        limit: this.recordsQuery.limit
      })
      if (data.status.code === 'success') {
        this.recordsList = data.data.records || []
        this.recordsTotal = data.data.total || 0
      }
    } catch (error) {
      this.$message.error('Failed to load records')
      console.error(error)
    } finally {
      this.recordsLoading = false
    }
  }

  private showSensorData(sensorData: any) {
    this.sensorDataJson = JSON.stringify(sensorData, null, 2)
    this.sensorDialogVisible = true
  }

  private formatDistance(meters: number | string): string {
    const m = Number(meters || 0)
    return (m / 1000).toFixed(1) + ' km'
  }

  private formatTime(seconds: number | string): string {
    const sec = Number(seconds || 0)
    return Math.round(sec / 60) + ' min'
  }

  private formatDate(date: string | Date | undefined): string {
    if (!date) return 'N/A'
    const d = new Date(date)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    const hours = d.getHours().toString().padStart(2, '0')
    const minutes = d.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }
}
